import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, Collapse, Image, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { renderExperimentsApi } from '../services/catalogServiceAPI/renderExperimentsProxy';
import { SquareImage, duration, num, pct } from './shared';

const { Title, Text } = Typography;
const GALLERY_SIZE = 200;

const buildArmColumns = (criteria) => [
    { title: 'Arm', dataIndex: 'label', render: (v, r) => <Space><b>{v}</b>{r.provider && <Text type="secondary">{r.provider} · {r.model}</Text>}</Space> },
    { title: 'Votes', dataIndex: 'votes', width: 70 },
    ...criteria.flatMap((c) => [
        { title: `${c.label}: mean (${c.min}–${c.max})`, key: `${c.id}-mean`, width: 150, render: (_, r) => num(r.criteria[c.id]?.mean) },
        { title: `${c.label}: wins`, key: `${c.id}-wins`, width: 110, render: (_, r) => pct(r.criteria[c.id]?.winRate) },
    ]),
    { title: 'Duration (mean / median)', key: 'duration', width: 180, render: (_, r) => r.kind === 'existing' ? '—' : `${duration(r.generation.meanDurationMs)} / ${duration(r.generation.medianDurationMs)}` },
    {
        title: 'Failure rate', key: 'failure', width: 140,
        render: (_, r) => r.kind === 'existing' ? '—' : (
            <Space size={4}>
                <Text type={r.generation.failureRate > 0 ? 'danger' : undefined}>{pct(r.generation.failureRate)}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>({r.generation.failures}/{r.generation.attempts})</Text>
            </Space>
        ),
    },
];

const totalMean = (arm) => Object.values(arm.criteria).reduce((acc, c) => acc + (c.mean ?? 0), 0);
const byScore = (arms) => arms.slice().sort((a, b) => totalMean(b) - totalMean(a));

const scoresText = (scores) => (scores?.length ? scores.join(', ') : '—');

export function RenderExperimentResults() {
    const history = useHistory();
    const { id } = useParams();
    const [results, setResults] = useState(null);

    useEffect(() => {
        renderExperimentsApi.results(id).then(setResults).catch((e) => message.error(e.message));
    }, [id]);

    const criteria = results?.experiment.criteria ?? [];
    const armColumns = useMemo(() => buildArmColumns(criteria), [criteria]);

    if (!results) return <div style={{ padding: 24 }}><Spin /></div>;

    const { experiment, totalVotes, arms, raters, sources } = results;

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>{experiment.name} — results</Title>
                        <Text type="secondary">{totalVotes} votes from {raters.length} rater{raters.length === 1 ? '' : 's'} across {sources.length} sources. Higher mean is better; "wins" is how often the arm scored strictly above every other arm. Duration covers successful generations only; failure rate counts every attempt, including retries.</Text>
                    </div>
                    <Space>
                        <Button onClick={() => history.push(`/experiments/${id}`)}>Back to experiment</Button>
                        <Button type="primary" onClick={() => history.push(`/experiments/${id}/rate`)}>Rate more</Button>
                    </Space>
                </div>

                <Table dataSource={byScore(arms)} columns={armColumns} rowKey="armId" size="small" pagination={false} scroll={{ x: true }} />

                {raters.length > 0 && (
                    <Collapse
                        items={raters.map((r) => ({
                            key: r.raterName,
                            label: `${r.raterName} — ${r.votes} vote${r.votes === 1 ? '' : 's'}`,
                            children: <Table dataSource={byScore(r.arms)} columns={armColumns} rowKey="armId" size="small" pagination={false} scroll={{ x: true }} />,
                        }))}
                    />
                )}

                <div>
                    <Title level={4}>Per source</Title>
                    <Image.PreviewGroup>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {sources.map((s) => (
                                <Card key={s.sourceRenderingId} size="small" title={<Space><Text code>{s.sourceRenderingId}</Text><Tag>{s.votes} vote{s.votes === 1 ? '' : 's'}</Tag></Space>}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                                        <div>
                                            <SquareImage src={s.sourceUrl} alt="Original" size={GALLERY_SIZE} />
                                            <div style={{ marginTop: 4 }}><b>Original</b></div>
                                        </div>
                                        {s.candidates.map((c) => (
                                            <div key={c.candidateId} style={{ width: GALLERY_SIZE }}>
                                                {c.url
                                                    ? <SquareImage src={c.url} alt={c.label} size={GALLERY_SIZE} />
                                                    : <div style={{ width: GALLERY_SIZE, height: GALLERY_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 8 }}><Text type="secondary">{c.status}</Text></div>}
                                                <div style={{ marginTop: 4 }}><b>{c.label}</b></div>
                                                {criteria.map((k) => (
                                                    <div key={k.id}><Text type="secondary" style={{ fontSize: 12 }}>{k.label}: {scoresText(c.scores[k.id])}</Text></div>
                                                ))}
                                                {c.attempts > 0 && (
                                                    <div><Text type="secondary" style={{ fontSize: 12 }}>{duration(c.durationMs)}{c.failures ? ` · ${c.failures} failed attempt${c.failures === 1 ? '' : 's'}` : ''}</Text></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            ))}
                        </Space>
                    </Image.PreviewGroup>
                </div>
            </Space>
        </div>
    );
}
